



import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Mixins } from 'vue-mixin-decorator';
import { mapGetters, mapActions } from 'vuex';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

import Assets from '@/constants/assets.ts';
import formatDate from '@/shared/filters/formatDate.filter.ts';
import Icon, { IconEnum } from '@/shared/components/icon.component.vue';
import { Participant } from '@/manager/convocation/participant.model';
import Input, {
    InputType,
    InputStyle,
    InputLabelColor,
} from '@/shared/components/input.component.vue';
import Btn, { BtnColor, BtnWidth, BtnPredefinedStyle } from '@/shared/components/btn.component.vue';
import Tooltip, { TooltipColor } from '@/shared/components/Tooltip.component.vue';
import CheckIcon, { CheckIconColor } from '@/shared/components/CheckIcon.component.vue';
import FlatSelectorText, {
    FlatSelectorTextBorderBottom,
    FlatSelectorTextWeight,
} from '@/shared/components/FlatSelectorText.component.vue';
import FlatSelectorIcon from '@/shared/components/FlatSelectorIcon/FlatSelectorIcon.component.vue';
import TextArea from '@/shared/components/text-area.component.vue';
import Modal, { ModalTitleColor } from '@/shared/components/Modal.component.vue';
import { Note } from '@/manager/convocation/note.model';
import Spinner from '@/shared/components/Spinner.component.vue';
import WithSpinner from '@/shared/mixins/withSpinner.mixin.ts';
import Field from '@/shared/components/field.component.vue';
import Endpoints from '@/constants/endpoints';
import TwoLevelsSelector from '@/shared/components/twoLevelsSelector/TwoLevelsSelector.component.vue';
import { SecondLevelOptionModel } from '@/shared/models/secondLevelOption.model';
import NoteItem from '@/manager/convocation/views/convocationDetail/noteItem/noteItem.view.vue';
import FlatSelectorIconSwitch, {
    SwitchIconsOption ,
    IconSwitchColorSeparator,
} from '@/shared/components/FlatSelectorIconSwitch.component.vue';
import { Operation, Income, Expense } from '@/manager/sync/operation.model';

@Component({
    components: {
        Btn,
        Icon,
        Input,
        CheckIcon,
        Field,
        FlatSelectorIcon,
        FlatSelectorText,
        FlatSelectorIconSwitch,
        TextArea,
        Tooltip,
        Modal,
        TwoLevelsSelector,
        Spinner,
        NoteItem,
    },
    computed: {
        ...mapGetters({
            clientList: 'getClientList',
            employeeList: 'getEmployeeList',
            getEmployee: 'getEmployee',
            getClient: 'getClient',
        }),
    },
    methods: {
        ...mapActions({
            fetchClientList: 'fetchClientList',
            fetchEmployeeList: 'fetchEmployeeList',
        }),
    },
    filters: {
        formatDate,
    },
})
export default class SyncRow extends Mixins<WithSpinner> (WithSpinner) {
    @Prop() private rowNumber!: number;
    @Prop() private operationId!: string;
    @Prop() private operationTypeIncomeOptions!: any[];
    @Prop() private operationTypeExpenseOptions!: any[];
    @Prop() private delegationOptions!: any[];
    @Prop() private productOptions!: any[];

    private assets = Assets;
    private iconEnum = IconEnum;
    private inputType = InputType;
    private inputStyle = InputStyle;
    private inputLabelColor = InputLabelColor;
    private tooltipColor = TooltipColor;
    private btnWidth = BtnWidth;
    private btnColor = BtnColor;
    private btnPredefinedClass = BtnPredefinedStyle;
    private flatSelectorTextBorderBottom = FlatSelectorTextBorderBottom;
    private flatSelectorTextWeight = FlatSelectorTextWeight;
    private iconSwitchColorSeparator = IconSwitchColorSeparator;
    private switchIconIncomeOptions: SwitchIconsOption[] = [
        {
            id: 1,
            src: Assets.iconIncome,
        },
    ];
    private switchIconExpenseOptions: SwitchIconsOption[] = [
        {
            id: 1,
            src: Assets.iconExpense,
        },
        {
            id: 2,
            src: Assets.iconInvestment,
        },
    ];
    private clientList !: any;
    private employeeList !: any;
    private fetchClientList !: any;
    private fetchEmployeeList !: any;
    private getEmployee !: any;
    private getClient !: any;
    private operationType = '';
    private operationProductType = '';
    private operationDelegation = '';
    private operationProduct = '';
    private operationTrainer = '';
    private operationClient = '';

    private created() {
        this.spinner['minimumTimeToShow'] = 500;
    }

    @Watch('operationType')
    private watchOperationType(newVal: any, oldVal: any) {
        let inversion = false;
        if (newVal === this.switchIconExpenseOptions[1]) {
            inversion = true;
        }
        this.patchOperation({ inversion });
    }

    @Watch('operationProductType')
    private watchOperationProductType(newVal: any, oldVal: any) {
        this.patchOperation({ type: newVal.value });
    }

    @Watch('operationDelegation')
    private watchOperationDelegation(newVal: any, oldVal: any) {
        this.patchOperation({ delegation: newVal.value });
    }

    @Watch('operationProduct')
    private watchOperationProduct(newVal: any, oldVal: any) {
        this.patchOperation({ product: newVal.value });
    }

    @Watch('operationTrainer')
    private watchOperationTrainer(newVal: any, oldVal: any) {
        this.patchOperation({ trainer: newVal.user.id }, () => {
            this.operation.trainer = newVal;
        });
    }

    @Watch('operationClient')
    private watchOperationClient(newVal: any, oldVal: any) {
        this.patchOperation({ client: newVal.user.id }, () => {
            this.operation.client = newVal;
        });
    }

    get switchIconSelected() {
        let switchIconSelected;
        if (this.isIncome) {
            switchIconSelected = this.switchIconIncomeOptions[0];
        } else if (this.isInversion) {
            switchIconSelected = this.switchIconExpenseOptions[1];
        } else {
            switchIconSelected = this.switchIconExpenseOptions[0];
        }
        return switchIconSelected;
    }

    get typeSelected() {
        const type = this.operation.type;
        return this.getSelectorObject(type, this.operationTypeList);
    }

    get delegationSelected() {
        const delegation = this.operation.delegation;
        return this.getSelectorObject(delegation, this.delegationOptions);
    }

    get productSelected() {
        const product = this.operation.product;
        return this.getSelectorObject(product, this.productOptions);
    }

    get clientSelected() {
        return this.operation.client;
    }

    get hasConcept() {
        return (this.operation.concept as any).trim().length > 0;
    }

    get operationTypeList() {
        let result = this.operationTypeIncomeOptions;
        if ( this.operation.operationType === Expense) {
            result = this.operationTypeExpenseOptions;
        }
        return result;
    }

    get operation(): Operation {
        return this.$store.getters.getOperation(this.operationId);
    }

    get oddRow() {
        return ( this.rowNumber % 2 !== 0 );
    }

    get colorSeparator() {
        return this.iconSwitchColorSeparator.Primary;
    }

    get switchIconOptions() {
        let result = this.switchIconExpenseOptions;
        if (this.isIncome) {
            result = this.switchIconIncomeOptions;
        }
        return result;
    }

    get isIncome() {
        return this.operation.operationType === Income;
    }

    get isExpense() {
        return this.operation.operationType === Expense;
    }

    get isInversion() {
        return this.operation.operationType === Expense && this.operation.inversion;
    }

    get flatBorderBottom() {
        let result = this.flatSelectorTextBorderBottom.Primary;
        if ( this.isExpense ) {
            result = this.flatSelectorTextBorderBottom.Secondary;
        }
        return result;
    }

    private clientSearch(searchObject: any) {
        this.fetchClientList({
            search: searchObject.searchString,
            limit: searchObject.limit,
        });
    }

    private employeeSearch(searchObject: any) {
        this.fetchEmployeeList({
            search: searchObject.searchString,
            limit: searchObject.limit,
        });
    }

    private getSelectorObject(value: any, list: any) {
        const optionSelected = list.find(
            (option: any) => {
                return (
                    value !== null &&
                    value !== undefined &&
                    option.value === value
                );
            },
        );
        let result;
        if ( value !== null && optionSelected !== undefined ) {
            let color = optionSelected.colorSelected;
            if (this.isInversion) {
                color = 'primary';
            }
            result = { value, label: (optionSelected as any).label, colorSelected: color };
        }
        return result;
    }

    private patchOperation(data: any, callback: any = () => { const a = 1; }) {
        const payload = {
            id: this.operationId,
            data,
        };
        this.$store.dispatch('patchOperation', payload)
            .then((res) => {
                callback();
            });
    }

}

